import {ChangeEvent, useCallback, useEffect, useState} from "react";
import { TQC2060_R01HeaderDto } from "./Dto_TQC2060_R01";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { DateTimeForm } from "src/components/common/form/DateTimeForm";
import { BRDiv } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { CodeForm } from "src/components/common/form/CodeForm";
import { getItemPopupList } from "src/apis/itemApi";
import { getEmployeeList } from "src/apis/employeeApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

import { useTQC2060_R01Store } from "./Store_TQC2060_R01";
import { EquipmentDto } from "src/dtos/equipment/equipmentDto";

interface Props {
	useEquipmentCdList: EquipmentDto[];
	defaultTQC2060_R01HeaderRow: TQC2060_R01HeaderDto;
}

export const Info_TQC2060_R01 = ({ useEquipmentCdList, defaultTQC2060_R01HeaderRow }: Props) => {

	const {  editTQC2060_R01, setEditTQC2060_R01 } = useTQC2060_R01Store();
    const { carModelList, equipmentCdList, platingTypeList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();

	const handleEmpFoucsId = 'empNo_search';
	const handleItemFoucsId = 'itemNo_search';

    // Info 폼 코드 검색 필드 설정
    const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
        {
            fieldName: 'itemNo',
            category: "품목",
            focusId: handleItemFoucsId,
            responseMapping: {
                itemCd: 'itemNo',  //  API의 itemCd를 DTO의 itemNo로 매핑
                itemNm: 'itemNm'
            },
            searchFunction: async (code: string) => {
                const itemResponse = await getItemPopupList(code, '', '', '');
                return itemResponse.data.data.find(value => value.itemCd === code);
            }
        },
        {
            fieldName: 'empNo',
            category: '시험자',
            focusId: handleEmpFoucsId,
            responseMapping: {
                empNo: 'empNo',
                empNm: 'empNm'
            },
            searchFunction: async (code: string) => {
                const employeeResponse = await getEmployeeList('all');
                return employeeResponse.data.data.find(value => value.empNo === code);
            }
        }
    ];

    // Info 폼 이벤트 훅 사용
    const { getFieldHandlers: originalGetFieldHandlers } = useInfoFormEvents({
        editData: editTQC2060_R01,
        setEditData: setEditTQC2060_R01,
        defaultData: defaultTQC2060_R01HeaderRow,
        infoFieldConfigs,
        setCodeSearchModalProps,
        formName: 'TQC2060_R01'
    });

	// getFieldHandlers를 래핑하여 값 변경 시 nm 초기화 로직 추가
	const getFieldHandlers = useCallback((fieldName: string) => {
		const originalHandlers = originalGetFieldHandlers(fieldName);
		
		// 커스텀 onChange 핸들러 생성
		const customOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			if (!editTQC2060_R01) return;
			
			// 기본 핸들러 호출
			originalHandlers.onChange(e);
		};
		
		return {
			...originalHandlers,
			onChange: customOnChange
		};
	}, [originalGetFieldHandlers, editTQC2060_R01, setEditTQC2060_R01, defaultTQC2060_R01HeaderRow]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		if (!editTQC2060_R01) return;

		// 시험기기 선택 시 설비명도 함께 업데이트
		if (name === 'equipmentCd') {
			const selectedEquipment = equipmentCdList.find(eq => eq.equipmentCd === value);
			setEditTQC2060_R01({ 
				...editTQC2060_R01, 
				[name]: value,
				equipmentNm: selectedEquipment?.equipmentNm || ''
			});
		} else if (name === 'sampleCount') {
			// 시료수는 숫자로 변환하여 저장
			const numValue = Number(value) || 0;
			setEditTQC2060_R01({ ...editTQC2060_R01, [name]: numValue });
		} else {
			setEditTQC2060_R01({ ...editTQC2060_R01, [name]: value });
		}
	}, [editTQC2060_R01, setEditTQC2060_R01, equipmentCdList]);

	// 날짜+시간 변경 핸들러 (ISO 문자열로 저장, 24시간 형식)
	const handleDateTimeChange = useCallback((field: keyof TQC2060_R01HeaderDto, value: Date | undefined) => {
		if (!editTQC2060_R01) return;
		
		// toISOString()은 UTC로 변환하므로, 현지 시간을 유지하는 형태로 변환
		let dateTimeValue: string | undefined;
		if (value) {
			const year = value.getFullYear();
			const month = String(value.getMonth() + 1).padStart(2, '0');
			const day = String(value.getDate()).padStart(2, '0');
			const hours = String(value.getHours()).padStart(2, '0');
			const minutes = String(value.getMinutes()).padStart(2, '0');
			const seconds = String(value.getSeconds()).padStart(2, '0');
			
			// 24시간 형식으로 저장 (DateTimeForm에서 이미 24시간으로 변환됨)
			dateTimeValue = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
		}
		
		setEditTQC2060_R01({ ...editTQC2060_R01, [field]: dateTimeValue });
	}, [editTQC2060_R01, setEditTQC2060_R01]);

	// 날짜 팝업 상태
	const [testFrdtVisible, setTestFrdtVisible] = useState(false);
	const [testFrdtPickerPos, setTestFrdtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});
	const [testTodtVisible, setTestTodtVisible] = useState(false);
	const [testTodtPickerPos, setTestTodtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 시험기기 자동 선택: equipmentNm이 있지만 equipmentCd가 없을 때 역매칭하여 설정
	useEffect(() => {
		if (!editTQC2060_R01 || !useEquipmentCdList.length) return;

		// equipmentNm은 있지만 equipmentCd가 비어있는 경우
		if (editTQC2060_R01.equipmentNm && !editTQC2060_R01.equipmentCd) {
			const matchedEquipment = useEquipmentCdList.find(
				eq => eq.equipmentNm === editTQC2060_R01.equipmentNm
			);
			
			if (matchedEquipment) {
				setEditTQC2060_R01({
					...editTQC2060_R01,
					equipmentCd: matchedEquipment.equipmentCd,
					equipmentNm: matchedEquipment.equipmentNm
				});
			}
		}

		// 옵션이 하나이고 equipmentCd가 비어있을 때 자동 선택
		if (useEquipmentCdList.length === 1 && !editTQC2060_R01.equipmentCd) {
			setEditTQC2060_R01({
				...editTQC2060_R01,
				equipmentCd: useEquipmentCdList[0].equipmentCd,
				equipmentNm: useEquipmentCdList[0].equipmentNm
			});
		}
	}, [useEquipmentCdList, editTQC2060_R01, setEditTQC2060_R01])

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
			<InputForm 
				title="시험번호" 
				value={editTQC2060_R01?.testResultNo || ''} 
				editable={false} 
				name="testResultNo" 
				onChange={handleChange} 
			/>
			<BRDiv />
			<InputForm 
				title="시험명" 
				value={editTQC2060_R01?.testNm || ''} 
				editable={true} 
				name="testNm" 
				onChange={handleChange} 
			/>
            <CodeForm
                label="품목코드"
                value={editTQC2060_R01?.itemNo || ""}
                custNmValue={editTQC2060_R01?.itemNm || ""}
                editable={true}
                name="itemNo"
                handleFoucsId={handleItemFoucsId}
                handleChange={getFieldHandlers('itemNo').onChange}
                onDoubleClick={getFieldHandlers('itemNo').onDoubleClick}
                onKeyDown={getFieldHandlers('itemNo').onEnterKeyDown}
            />
			<BRDiv />
            <DropDownForm
                title="도금구분"
                value={editTQC2060_R01?.platingType || ''}
                name="platingType"
                initLabelLess={true}
                data={platingTypeList}
                editable={true}
                onChange={handleChange}
            />
			<BRDiv />
			<CodeForm
				label="시험자"
				value={editTQC2060_R01?.empNo || ""}
				custNmValue={editTQC2060_R01?.empNm || ""}
				editable={true}
				name="empNo"
				handleFoucsId={handleEmpFoucsId}
				handleChange={getFieldHandlers('empNo').onChange}
				onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
				onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
			/>
			<DropDownForm
				title="차종" 
				value={editTQC2060_R01?.carModel || ''} 
				name="carModel"
				initLabelLess={true}
				data={carModelList}
				editable={true}
				onChange={handleChange} 
			/>
			<BRDiv />
			<DateTimeForm
				title="시험기간 시작"
				value={editTQC2060_R01?.testFrdt ? new Date(editTQC2060_R01.testFrdt) : undefined}
				setValue={(value: Date | undefined) => handleDateTimeChange('testFrdt', value)}
				editable={true}
				visible={testFrdtVisible}
				setVisible={setTestFrdtVisible}
				onChange={handleChange}
				pickerPos={testFrdtPickerPos}
				setPickerPos={setTestFrdtPickerPos}
				placeholder="시험 시작일 선택"
				showSeconds={false}
				hidePickerTime={true}
				wrapperOption={{
					wrapperStyle: "w-[300px] min-w-[300px] mr-[10px] mb-[5px]",
					titleStyle: "w-[100px] min-w-[100px] mr-[5px] text-left",
					inputStyle: "w-[110px] min-w-[110px] mr-[5px]",
					timeInputStyle: "w-[80px] min-w-[80px]",
				}}
			/>
			<DateTimeForm
				title="시험기간 종료"
				value={editTQC2060_R01?.testTodt ? new Date(editTQC2060_R01.testTodt) : undefined}
				setValue={(value: Date | undefined) => handleDateTimeChange('testTodt', value)}
				editable={true}
				visible={testTodtVisible}
				setVisible={setTestTodtVisible}
				onChange={handleChange}
				pickerPos={testTodtPickerPos}
				setPickerPos={setTestTodtPickerPos}
				placeholder="시험 종료일 선택"
				showSeconds={false}
				hidePickerTime={true}
				wrapperOption={{
					wrapperStyle: "w-[300px] min-w-[300px] mr-[10px] mb-[5px]",
					titleStyle: "w-[100px] min-w-[100px] mr-[5px] text-left",
					inputStyle: "w-[110px] min-w-[110px] mr-[5px]",
					timeInputStyle: "w-[80px] min-w-[80px]",
				}}
			/>
			<BRDiv />
			<DropDownForm
				title="시험기기" 
				value={editTQC2060_R01?.equipmentCd || ''} 
				name="equipmentCd"
				initLabelLess={true}
				data={useEquipmentCdList}
				editable={true}
				onChange={handleChange} 
			/>
			
			
			<BRDiv />
			<InputForm title="판정조건" value={editTQC2060_R01?.resultCondition || ''} editable={true} name="resultCondition" onChange={handleChange} />
			<InputForm 
				title="시료수" 
				value={editTQC2060_R01?.sampleCount || 0} 
				editable={true} 
				name="sampleCount" 
				type="string"
				onChange={handleChange} 
			/>
			<BRDiv />
			<InputForm title="종합판정" value={editTQC2060_R01?.resultValue || ''} editable={true} name="resultValue" onChange={handleChange} />
		</div>
	)
}
